import { useEffect, useState } from 'react';
import { FormattedNumber } from 'react-intl';
import { toast } from 'react-toastify';
import { useCallbackObservable, useObservable } from 'react-use-observable';

import { Button } from 'antd';

import MyTable, { MyTableBody, MyTableCol, MyTableHeader, MyTableRow } from 'components/MyTable';
import type { IMyTableHeaderIndex } from 'components/MyTable/MyTableHeader';
import type { FormikProps } from 'formik';
import { Field, Form, Formik } from 'formik';
import { tap } from 'rxjs/operators';
import type { IOrder } from 'v2/components/Pages/Orders/interfaces';
import { TextFormField } from 'v2/components/Shared/FormFields/TextFormField';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import ordersService from 'v2/services/orders';
import { isRequired } from 'validations/defaultMessages';
import * as Yup from 'yup';

const trackingSchema = Yup.object().shape({
  traking_code: Yup.string().required(isRequired('código de rastreio'))
});

const headers: IMyTableHeaderIndex[] = [
  { title: 'Fatura', ordering: false },
  { title: 'Cliente', ordering: false },
  { title: 'Cidade/Estado', ordering: false },
  { title: 'CEP', ordering: false },
  { title: 'Ganho', ordering: false },
  { title: '', ordering: false },
  { title: '', ordering: false }
];

const List = () => {
  const [alreadySent, setAlreadySent] = useState<number[]>([]);
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 599.95px)').matches);

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 599.95px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  const [orders] = useObservable(() => ordersService.getOrdersToSendDeliveryCode(), []);

  const [onSendDeliveryCode] = useCallbackObservable((values: IOrder, form: FormikProps<IOrder>) => {
    return ordersService.sendDeliveryCodeOrder(values).pipe(
      tap(() => {
        try {
          setAlreadySent(prev => [...prev, values.invoice_id]);
          form.setSubmitting(false);
          toast.success(`Código de rastreio ${values.traking_code} enviado com sucesso`);
        } catch (error) {
          toast.error(error);
        }
      })
    );
  }, []);

  return (
    <MyTable responsive>
      <MyTableHeader index={headers} />
      <MyTableBody>
        {(!orders || !orders.length) && (
          <tr>
            <td colSpan={headers.length}>Nenhum registro encontrado.</td>
          </tr>
        )}
        {Array.isArray(orders) &&
          orders.map((order: IOrder, index: number) => {
            return (
              <Formik
                key={`order-${order.invoice_id}`}
                initialValues={order}
                validationSchema={trackingSchema}
                onSubmit={onSendDeliveryCode}
              >
                {({ isSubmitting, values, dirty, isValid }: FormikProps<IOrder>) => {
                  const sent = alreadySent?.includes(values.invoice_id);
                  const hideTrackingCodeColumn = isMobile && sent;
                  return (
                    <MyTableRow>
                      <MyTableCol screenSizes={{ xs: 6, sm: 6, md: 6, lg: 3 }} mobileOrder={4}>
                        {values.invoice_id}
                      </MyTableCol>
                      <MyTableCol screenSizes={{ xs: 0, sm: 0, md: 6, lg: 3 }} mobileOrder={1}>
                        <PermissionHide action='myeduzz:minhas-entregas:ver-dados-clientes' fallback={<em>oculto</em>}>
                          {values.customer.name}
                        </PermissionHide>
                      </MyTableCol>
                      <MyTableCol screenSizes={{ xs: 0, sm: 0, md: 6, lg: 3 }} mobileOrder={5}>
                        <PermissionHide action='myeduzz:minhas-entregas:ver-dados-clientes' fallback={<em>oculto</em>}>
                          {`${values.customer.address.city}/${values.customer.address.state}`}
                        </PermissionHide>
                      </MyTableCol>
                      <MyTableCol screenSizes={{ xs: 0, sm: 0, md: 6, lg: 3 }} mobileOrder={6}>
                        <PermissionHide action='myeduzz:minhas-entregas:ver-dados-clientes' fallback={<em>oculto</em>}>
                          {values.customer.address.zip_code}
                        </PermissionHide>
                      </MyTableCol>
                      <MyTableCol screenSizes={{ xs: 6, sm: 6, md: 6, lg: 3 }} mobileOrder={7} numeric>
                        <PermissionHide inverse action='myeduzz:minhas-entregas:ocultar-valores'>
                          <FormattedNumber format='BRL' value={values.net_income} />
                        </PermissionHide>
                        <PermissionHide inverse={false} action='myeduzz:minhas-entregas:ocultar-valores'>
                          <em>oculto</em>
                        </PermissionHide>
                      </MyTableCol>

                      {!hideTrackingCodeColumn && (
                        <MyTableCol screenSizes={{ xs: 12, sm: 12, md: 6, lg: 4 }} mobileOrder={8}>
                          <Form>
                            <div className='flex flex-row gap-2'>
                              <Field
                                placeholder='Código'
                                id={`tracking_code${index}`}
                                name={'traking_code'}
                                fullWidth
                                disabled={sent}
                                value={values.traking_code ? values.traking_code : ''}
                                InputLabelProps={{ shrink: true }}
                                component={TextFormField}
                              />

                              <Button
                                disabled={isSubmitting || !dirty || !isValid || !!sent}
                                htmlType='submit'
                                type='primary'
                                style={{ minWidth: 'auto' }}
                                block
                              >
                                {sent ? 'Enviado' : isSubmitting ? 'Enviando ...' : 'Enviar'}
                              </Button>
                            </div>
                          </Form>
                        </MyTableCol>
                      )}
                    </MyTableRow>
                  );
                }}
              </Formik>
            );
          })}
      </MyTableBody>
    </MyTable>
  );
};
export default List;
